<script setup>
import {setSelectAmountService} from "@/stores/Services";
import {useMediaQuery} from "@vueuse/core";
import {ref} from "vue";
import Image from '@/components/Service/Image'
import Button from '@/components/Button'
import {formatPrice} from '@/libraries/Helpers'

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: true,
  },
  price: {
    type: Number,
    required: true,
  },
  currency: {
    type: String,
    required: true,
  },
  image: {
    type: String,
    required: true,
  },
  minAmount: {
    type: Number,
    required: true,
  },
  maxAmount: {
    type: Number,
    required: true,
  },
  selected: {
    type: Boolean,
    required: true,
  },
  selectedAmount: {
    type: Number,
    required: true,
  },
  disabled: {
    type: Boolean,
    required: true,
  },
  locked: {
    type: Boolean,
    required: false,
    default: false,
  },
  allowAmount: {
    type: Boolean,
    default: false
  }
})

const emits = defineEmits(['productAdd', 'productRemove'])

const amount = ref(props.selectedAmount)

const changeAmount = (value) => {
  let newValue
  if (value === 'inc') {
    newValue = amount.value + 1
  } else if (value === 'dec') {
    newValue = amount.value - 1
  } else {
    if (isNaN(value)) {
      newValue = 1
    } else {
      newValue = parseInt(value)
    }
  }

  if (props.minAmount >= newValue) {
    newValue = props.minAmount
  }

  if (newValue < 0) {
    newValue = 0
  }

  if (props.maxAmount && props.maxAmount < newValue) {
    newValue = props.maxAmount
  }

  amount.value = newValue

  if (props.selected && newValue) {
    addProduct(newValue)
  } else {
    removeProduct()
  }
}

const isLargeScreen = useMediaQuery('(min-width: 1024px)')

const addProduct = (addAmount = 0) => {
  const finalAmount = addAmount || amount.value

  if (!finalAmount) {
    return;
  }

  emits('productAdd', {
    id: props.id,
    amount: finalAmount,
    itemId: null
  })
}

const removeProduct = () => {
  emits('productRemove', {
    id: props.id,
    itemId: null
  })
}
</script>

<template>
  <div
    class="relative before:absolute before:bottom-0 before:left-0 before:right-0 before:h-1px before:bg-gray-400 after:absolute after:-top-1px after:left-0 after:right-0 after:h-1px after:bg-gray-400"
  >
    <div class="flex flex-wrap py-12 lg:flex-nowrap">
      <div class="w-full mb-4" v-if="!isLargeScreen">
        <h3 class="text-black font-medium lg:text-16px mb-0">{{ name }}</h3>
        <article class="mt-4" v-if="description" v-html="description"></article>
      </div>

      <Image :name="name" :image="image" :zoom="true"/>

      <div
        class="flex-grow flex flex-col w-7/12 pr-5 sm:pr-13 lg:w-full lg:pr-0 lg:mx-14"
      >
        <h3
          class="text-black font-medium mb-4 lg:block lg:text-16px"
          v-if="isLargeScreen"
        >
          {{ name }}
        </h3>
        <article
          v-if="description && isLargeScreen"
          v-html="description"
        ></article>

        <div class="flex items-center w-full lg:pt-6 lg:mt-auto">
          <button
            type="button"
            class="flex-shrink-0 w-16 h-16 text-12 text-blue-900 bg-gray-400 rounded-full mr-3 leading-none"
            :disabled="disabled"
            @click="changeAmount('dec')"
            v-if="!locked"
          >
            <span class="block -mt-2px">-</span>
          </button>
          <input
            type="number"
            inputmode="numeric"
            class="form-control text-center font-bold number-appearance-none max-w-62"
            :disabled="disabled"
            v-model="amount"
            @change="changeAmount($event.target.value)"
          />
          <button
            type="button"
            class="flex-shrink-0 w-16 h-16 text-12 text-blue-900 bg-gray-400 rounded-full ml-3 leading-none"
            :disabled="disabled"
            @click="changeAmount('inc')"
            v-if="!locked"
          >
            <span class="block -mt-2px">+</span>
          </button>
        </div>
      </div>

      <div
        class="flex-shrink-0 w-5/12 flex flex-col justify-between pl-5 sm:pl-13 lg:w-68 lg:pl-0"
      >
        <div class="order-2 mt-10 lg:order-1 lg:mt-0" v-if="!locked">
          <Button
            variant="secondary"
            size="full"
            :disabled="disabled"
            @click="addProduct(0)"
            v-if="!selected"
          >
            {{ __('btn_add') }}
          </Button>
          <Button
            variant="secondary"
            size="full"
            :disabled="disabled"
            @click="removeProduct"
            v-else
          >
            {{ __('btn_remove') }}
          </Button>
        </div>
        <div
          class="order-1 px-10 py-[0.6rem] border-2px border-gray-400 rounded-full text-center font-bold lg:order-2"
          :class="{ 'mt-auto': locked }"
        >
          {{ currency }}{{ formatPrice(price) }}
        </div>
      </div>
    </div>
  </div>
</template>